import { Box, Chip } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams, GridRowSelectionModel } from '@mui/x-data-grid';
import { FunctionComponent } from 'react';
import { QuestionSetConfig, QuestionSetId } from '../../types/GameplayTypes';
import { QuestionActions } from './QuestionActions';
import { Author } from './QuestionComponents';
import { useQuestionFilters } from './QuestionFilterContext';

type Props = {
	questionSets: QuestionSetConfig[];
	selectionModel: GridRowSelectionModel;
	onSelectionChange: (model: GridRowSelectionModel) => void;
	onEdit: (id: QuestionSetId) => void;
	onDelete: (id: QuestionSetId) => void;
	onDuplicate: (id: QuestionSetId) => void;
	onPlay?: (id: QuestionSetId) => void;
};

export const QuestionSetsList: FunctionComponent<Props> = (props) => {
	const { gridFilterModel, setGridFilterModel } = useQuestionFilters();

	const columns: GridColDef[] = [
		{ field: 'name', headerName: 'Name', width: 200 },
		{
			field: 'questionCount', headerName: 'Questions', type: 'number', width: 100,
			valueGetter: (_value, row) => row.questionIds.length,
		},
		{ field: 'description', headerName: 'Description', flex: 1, minWidth: 300 },
		{
			field: 'tags', headerName: 'Tags', width: 250,
			renderCell: (params: GridRenderCellParams<QuestionSetConfig>) => <Tags tags={params.row.tags} />
		},
		{ field: 'isActive', headerName: 'Active', type: 'boolean', width: 100 },
		{
			field: 'authorName',
			headerName: 'Author',
			width: 200,
			renderCell: (params: GridRenderCellParams<QuestionSetConfig>) => <Author question={params.row} />
		},
		{
			field: 'actions', headerName: 'Actions', width: 180, sortable: false,
			renderCell: (params: GridRenderCellParams<QuestionSetConfig>) =>
				<QuestionActions id={params.row.id} itemName='Question Set' {...props} />
		},
	];

	return <DataGrid
		rows={props.questionSets}
		columns={columns}
		initialState={{
			pagination: {
				paginationModel: { pageSize: 25 },
			},
		}}
		pageSizeOptions={[10, 25, 50, 100]}
		checkboxSelection
		disableRowSelectionOnClick
		rowSelectionModel={props.selectionModel}
		onRowSelectionModelChange={props.onSelectionChange}
		filterModel={gridFilterModel}
		onFilterModelChange={setGridFilterModel}
	/>;
};

const Tags: FunctionComponent<{ tags: string[]; }> = (props) =>
	<Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center', paddingY: 1 }}>
		{props.tags.map(tag =>
			<Chip key={tag} label={tag} size="small" />
		)}
	</Box>;

